import React, { useState, useEffect } from 'react';
import BackButton from '@/components/BackButton';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Money, CurrencyDollar, Download } from '@phosphor-icons/react';
import { Sar } from '@/components/SarIcon';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "/branding/logo.png";

const monthsMap = {
  ar: { '01': 'يناير', '02': 'فبراير', '03': 'مارس', '04': 'أبريل', '05': 'مايو', '06': 'يونيو',
        '07': 'يوليو', '08': 'أغسطس', '09': 'سبتمبر', '10': 'أكتوبر', '11': 'نوفمبر', '12': 'ديسمبر' },
  en: { '01': 'January', '02': 'February', '03': 'March', '04': 'April', '05': 'May', '06': 'June',
        '07': 'July', '08': 'August', '09': 'September', '10': 'October', '11': 'November', '12': 'December' },
};

export default function EmployeePayslips() {
  const { t } = useTranslation();
  const { language, direction } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [payslips, setPayslips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloadingId, setDownloadingId] = useState(null);

  // ---- Agency Contracted Workers: no payslip portal ----
  useEffect(() => {
    if (user?.is_contracted_worker) {
      navigate('/home', { replace: true });
    }
  }, [user, navigate]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const payRes = await axios.get(`${API}/my/payroll`, { withCredentials: true }).catch(() => ({ data: [] }));
      setPayslips(payRes.data || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPayslip = async (payrollId, month, year) => {
    setDownloadingId(payrollId);
    try {
      const response = await axios.get(`${API}/my/payroll/${payrollId}/payslip`, {
        withCredentials: true,
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `payslip_${month}_${year}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(language === 'ar' ? 'تم تحميل كشف الراتب' : 'Payslip downloaded');
    } catch {
      toast.error(language === 'ar' ? 'فشل التحميل' : 'Download failed');
    } finally {
      setDownloadingId(null);
    }
  };

  // v1.5.18 — per business policy, employees always see published
  // payslips as "معمد" (certified/approved). The internal "paid" vs
  // "pending" workflow is a finance-side concern; from the employee's
  // viewpoint any payslip visible in the portal has been officially
  // approved. This is a UI-only change — DB status is untouched.
  const getStatusBadge = () => 'bg-emerald-100 text-emerald-700 border-emerald-200';

  const monthName = (m) => {
    const padded = String(m ?? '').padStart(2, '0');
    return monthsMap[language]?.[padded] || m;
  };

  if (user?.is_contracted_worker) {
    return null;
  }

  return (
    <div className="min-h-screen bg-[#F2F0FA]" data-testid="employee-payslips-page" dir={direction}>
      <header className="sticky top-0 z-40 bg-white/95 dark:bg-slate-900/95 backdrop-blur-sm shadow-sm border-b border-[#C4BFF0] dark:border-slate-700 py-4">
        <div className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
          <img src={LOGO_URL} alt="Lucent" className="h-10 object-contain" />
          <BackButton className="text-[#655dc6]" />
        </div>
      </header>

      <main className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        <h1 className="text-3xl font-semibold text-[#655dc6]">{t('my_payslips')}</h1>

        {/* Payslips Table */}
        <Card className="border border-[#C4BFF0]">
          <CardHeader>
            <CardTitle className="text-lg font-medium text-[#655dc6] flex items-center gap-2">
              <Money size={22} weight="duotone" className="text-[#655dc6]" />
              {t('payroll')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center h-32">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#655dc6]" />
              </div>
            ) : (
              <>
              {/* Mobile Card List (<640px) — one vertical card per payslip. */}
              <div className="sm:hidden space-y-3" data-testid="payslips-mobile-list">
                {payslips.length === 0 ? (
                  <p className="text-center py-8 text-[#655dc6]/70 text-sm">{t('no_data')}</p>
                ) : (
                  payslips.map((payslip) => {
                    const isAdj = payslip.payroll_type === 'adjustment';
                    return (
                      <div
                        key={payslip.id}
                        data-testid={`payslip-card-${payslip.id}`}
                        className="bg-white dark:bg-slate-900 border border-[#C4BFF0] dark:border-slate-700 rounded-xl p-4 shadow-sm"
                      >
                        {/* Header: period + status */}
                        <div className="flex items-start justify-between gap-2 pb-3 mb-3 border-b border-[#C4BFF0]/40 dark:border-slate-800/80">
                          <div className="min-w-0">
                            <h4 className="text-base font-bold text-[#262263] dark:text-slate-100">
                              {monthName(payslip.month)} {payslip.year}
                            </h4>
                            {isAdj && (
                              <Badge className="mt-1 bg-amber-100 text-amber-800 border-amber-200 text-[10px]" data-testid={`adj-badge-mobile-${payslip.id}`}>
                                {language === 'ar' ? 'تعديل' : 'Adjustment'}
                              </Badge>
                            )}
                          </div>
                          <Badge className={`${getStatusBadge()} shrink-0 text-[11px]`} data-testid={`payslip-status-mobile-${payslip.id}`}>
                            {language === 'ar' ? 'معمد' : 'Approved'}
                          </Badge>
                        </div>

                        {/* Metadata grid */}
                        {!isAdj && (
                          <div className="grid grid-cols-2 gap-2 text-xs mb-3">
                            <div>
                              <span className="block text-[#655dc6]/60 dark:text-slate-500 font-medium mb-0.5">{t('basic_salary')}</span>
                              <span className="font-semibold text-slate-800 dark:text-slate-200">
                                {payslip.basic_salary?.toLocaleString()} <Sar />
                              </span>
                            </div>
                            <div>
                              <span className="block text-[#655dc6]/60 dark:text-slate-500 font-medium mb-0.5">{t('allowances')}</span>
                              <span className="font-semibold text-green-600">
                                +{payslip.allowances?.toLocaleString()} <Sar />
                              </span>
                            </div>
                            {(payslip.overtime_amount || 0) > 0 && (
                              <div>
                                <span className="block text-[#655dc6]/60 dark:text-slate-500 font-medium mb-0.5">{language === 'ar' ? 'الإضافي' : 'Overtime'}</span>
                                <span className="font-semibold text-blue-600">
                                  +{(payslip.overtime_amount || 0).toLocaleString()} <Sar />
                                </span>
                              </div>
                            )}
                            <div>
                              <span className="block text-[#655dc6]/60 dark:text-slate-500 font-medium mb-0.5">{t('deductions')}</span>
                              <span className="font-semibold text-red-600">
                                -{payslip.deductions?.toLocaleString()} <Sar />
                              </span>
                            </div>
                          </div>
                        )}

                        {/* Footer: net + download */}
                        <div className="pt-3 flex items-center justify-between gap-2 border-t border-[#C4BFF0]/40 dark:border-slate-800/80">
                          <div>
                            <span className="block text-[10px] text-[#655dc6]/60 dark:text-slate-500 uppercase tracking-wide">{t('net_salary')}</span>
                            <span className="text-lg font-extrabold text-[#655dc6] dark:text-violet-300">
                              {isAdj && '+'}{payslip.net_salary?.toLocaleString()} <Sar />
                            </span>
                          </div>
                          <Button
                            size="sm"
                            onClick={() => handleDownloadPayslip(payslip.id, payslip.month, payslip.year)}
                            disabled={downloadingId === payslip.id}
                            className="bg-[#655dc6] hover:bg-[#544cb0] text-white gap-1.5 min-h-[44px] px-4"
                            data-testid={`download-payslip-mobile-${payslip.id}`}
                          >
                            {downloadingId === payslip.id ? (
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                            ) : (
                              <>
                                <Download size={16} />
                                <span>PDF</span>
                              </>
                            )}
                          </Button>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

              {/* Desktop Table (>=640px) */}
              <div className="hidden sm:block overflow-x-auto scrollbar-thin" data-mobile-hint="scroll-x">
                <Table data-testid="payslips-table">
                  <TableHeader>
                    <TableRow className="border-[#C4BFF0]">
                      <TableHead className="text-start">{t('period')}</TableHead>
                      <TableHead className="text-start hidden md:table-cell">{t('basic_salary')}</TableHead>
                      <TableHead className="text-start hidden lg:table-cell">{t('allowances')}</TableHead>
                      <TableHead className="text-start hidden lg:table-cell">{language === 'ar' ? 'الإضافي' : 'Overtime'}</TableHead>
                      <TableHead className="text-start hidden md:table-cell">{t('deductions')}</TableHead>
                      <TableHead className="text-start">{t('net_salary')}</TableHead>
                      <TableHead className="text-start hidden sm:table-cell">{t('status')}</TableHead>
                      <TableHead className="text-start">{language === 'ar' ? 'تحميل' : 'Download'}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {payslips.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center py-8 text-[#655dc6]/70">
                          {t('no_data')}
                        </TableCell>
                      </TableRow>
                    ) : (
                      payslips.map((payslip) => {
                        const isAdj = payslip.payroll_type === 'adjustment';
                        return (
                        <TableRow key={payslip.id} className="border-[#C4BFF0]" data-testid={`payslip-row-${payslip.id}`}>
                          <TableCell className="font-medium text-[#655dc6]">
                            <div className="whitespace-nowrap">
                              {monthName(payslip.month)} {payslip.year}
                              {isAdj && (
                                <Badge className="ms-2 bg-amber-100 text-amber-800 border-amber-200" data-testid={`adj-badge-${payslip.id}`}>
                                  {language === 'ar' ? 'تعديل' : 'Adjustment'}
                                </Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-[#655dc6]/70 hidden md:table-cell">
                            {isAdj ? '—' : payslip.basic_salary?.toLocaleString()} {!isAdj && <Sar />}
                          </TableCell>
                          <TableCell className="text-green-600 hidden lg:table-cell">
                            {isAdj ? '—' : <>+{payslip.allowances?.toLocaleString()} <Sar /></>}
                          </TableCell>
                          <TableCell className="text-blue-600 hidden lg:table-cell">
                            {isAdj ? '—' : <>+{(payslip.overtime_amount || 0).toLocaleString()} <Sar /></>}
                          </TableCell>
                          <TableCell className="text-red-600 hidden md:table-cell">
                            {isAdj ? '—' : <>-{payslip.deductions?.toLocaleString()} <Sar /></>}
                          </TableCell>
                          <TableCell className="font-semibold text-[#655dc6] whitespace-nowrap">
                            {isAdj && '+'}{payslip.net_salary?.toLocaleString()} <Sar />
                          </TableCell>
                          <TableCell className="hidden sm:table-cell">
                            <Badge className={getStatusBadge()} data-testid={`payslip-status-${payslip.id}`}>
                              {language === 'ar' ? 'معمد' : 'Approved'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDownloadPayslip(payslip.id, payslip.month, payslip.year)}
                              disabled={downloadingId === payslip.id}
                              className="text-[#655dc6] hover:bg-[#E8E6F5] gap-1"
                              data-testid={`download-payslip-${payslip.id}`}
                            >
                              {downloadingId === payslip.id ? (
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#655dc6]" />
                              ) : (
                                <>
                                  <Download size={16} />
                                  <span className="hidden sm:inline">PDF</span>
                                </>
                              )}
                            </Button>
                          </TableCell>
                        </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </div>
              </>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
